import type { ReactNode } from "react";
import NewReservationButton from "./NewReservationButton";

/**
 * Редът над платното. На широк екран (списъкът е вдясно) — само табовете на
 * регионите: ‹ ден › и „+ Нова резервация" са над списъка
 * (`ReservationsSideControls`), затова `dayNavigator` е null. Когато
 * списъкът е под платното — табовете вляво (скрол, когато не се събират),
 * денят и бутонът вдясно на същия ред; на телефон денят и бутонът (с текст)
 * са на първия ред наполовина, табовете — под тях. Без регион (зарежда се, грешка, няма
 * схема) — само денят, за да може да се смени. Всичко е 36px. `extraControl`
 * (напр. „Скрий до 1-ва резервация" в таблицата) е между деня и бутона; на телефон —
 * на свой ред под тях.
 */
const FloorToolbar = ({
  regionTabs,
  dayNavigator,
  onNewReservation,
  isNarrow,
  extraControl = null,
}: {
  // null — няма регион (табовете не се показват).
  regionTabs: ReactNode;
  // null — денят е другаде (над списъка).
  dayNavigator: ReactNode;
  // null — бутонът е скрит (напр. схемата още се зарежда) или е над списъка.
  onNewReservation: (() => void) | null;
  isNarrow: boolean;
  extraControl?: ReactNode;
}) => {
  const hasControls = Boolean(dayNavigator) || onNewReservation !== null || extraControl !== null;
  const controls = hasControls && (
    <div className={isNarrow ? "grid grid-cols-2 gap-2" : "flex shrink-0 items-center gap-2"}>
      {dayNavigator}
      {extraControl && <div className={isNarrow ? "order-last col-span-2" : "contents"}>{extraControl}</div>}
      {onNewReservation && (
        <NewReservationButton
          onClick={onNewReservation}
          className={isNarrow ? "w-full" : "@max-[640px]:w-9 @max-[640px]:px-0 @max-[640px]:[&>span]:hidden"}
        />
      )}
    </div>
  );

  if (isNarrow) {
    return (
      <div className="flex shrink-0 flex-col gap-2.5">
        {controls}
        {regionTabs}
      </div>
    );
  }

  // Един ред: табовете взимат останалото място (min-w-0 → скрол вътре в
  // тях), контролите не се свиват.
  return (
    <div className="@container flex min-h-9 shrink-0 items-center gap-3">
      {regionTabs ? <div className="min-w-0 flex-1">{regionTabs}</div> : <div className="flex-1" />}
      {controls}
    </div>
  );
};

export default FloorToolbar;
